import { useEffect, useRef, useState } from "react";

import { api } from "../lib/api";
import { identifierAsHex } from "../lib/identifiers";

export interface JobProgressEvent {
  type: string;
  [k: string]: unknown;
}

interface Props {
  jobId: string;
  onDone: (ev: JobProgressEvent) => void;
  onFailed: (ev: JobProgressEvent) => void;
  /** If provided, render a Cancel button that calls this. Hidden when not
   * provided (parent decides whether cancel is offered for this job). */
  onCancel?: () => void;
}

interface Counts { done: number; total: number | null }

/** Small progress card wired to the job's SSE stream. */
export function JobProgress({ jobId, onDone, onFailed, onCancel }: Props) {
  const [cancelArmed, setCancelArmed] = useState(false);
  const [status, setStatus] = useState<string>("connecting…");
  const [counts, setCounts] = useState<Counts>({ done: 0, total: null });
  const [elapsedLog, setElapsedLog] = useState<string[]>([]);
  const [stationDurations, setStationDurations] = useState<number[]>([]);
  const [jobStart, setJobStart] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState<number>(Date.now());
  const [terminal, setTerminal] = useState<boolean>(false);

  const esRef = useRef<EventSource | null>(null);
  // Hold the latest callback identities in refs so the SSE effect only
  // depends on jobId. Without this, parents that recompute onDone/onFailed
  // on every render (e.g. inline arrows captured during a 4s poll) cause
  // the SSE to reconnect — which re-delivers the "snapshot" event and
  // double-appends each station_done into elapsedLog.
  const onDoneRef = useRef(onDone);
  const onFailedRef = useRef(onFailed);
  onDoneRef.current = onDone;
  onFailedRef.current = onFailed;

  // Track which station_done events we've already applied so a snapshot
  // re-delivery (e.g. SSE reconnect) can't double-count.
  const seenStationsRef = useRef<Set<string>>(new Set());

  // Has the terminal (done/failed) onDone/onFailed callback already fired?
  // Used to dedupe the case where SSE delivers a snapshot AND a fresh event
  // for the same terminal status.
  const terminalFiredRef = useRef<boolean>(false);

  // 1-Hz heartbeat so the elapsed counter ticks even when no SSE events arrive.
  useEffect(() => {
    if (terminal) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [terminal]);

  useEffect(() => {
    const es = new EventSource(api.eventsUrl(jobId));
    esRef.current = es;
    es.addEventListener("snapshot", (e) => handle(JSON.parse((e as MessageEvent).data)));
    es.addEventListener("update", (e) => handle(JSON.parse((e as MessageEvent).data)));
    es.onerror = () => setStatus("disconnected — retrying");

    function handle(ev: JobProgressEvent) {
      if (ev.type === "started") {
        setCounts({ done: 0, total: Number(ev.stations_in_scope) });
        setStatus(`running · ${ev.stations_in_scope} stations · ${Number(ev.bbox_diagonal_mi).toFixed(1)} mi diag`);
        setJobStart((prev) => prev ?? Date.now());
      } else if (ev.type === "station_done") {
        const key = `${ev.index}:${ev.identifier}`;
        if (seenStationsRef.current.has(key)) return;
        seenStationsRef.current.add(key);
        const dur = Number(ev.elapsed_sec);
        const total = Number(ev.total);
        setCounts({ done: Number(ev.index), total });
        setStationDurations((l) => [...l, dur]);
        setElapsedLog((l) => [...l.slice(-6),
          `${identifierAsHex(String(ev.identifier))} — ${dur.toFixed(1)}s`]);
        setJobStart((prev) => prev ?? Date.now());
        // SSE backend only delivers the latest event as a snapshot, so when
        // the UI attaches mid-job there's no "started" event to drive the
        // status header — derive it from the station_done snapshot instead.
        setStatus((s) => (s === "connecting…" || s.startsWith("disconnected"))
          ? `running · ${total} stations`
          : s);
      } else if (ev.type === "finalizing") {
        setStatus("finalizing (COG + overviews)…");
      } else if (ev.type === "done") {
        // SSE delivers BOTH a snapshot of the latest state AND a fresh
        // event for the same status, so "done" can fire twice in close
        // succession. Without this dedupe, parent onDone handlers run
        // twice in parallel — caused a duplicate-addLayer race where
        // two MinAltitudeRasterLayer instances raced to add the same
        // layer id, failing one and leaving vsLayerRef pointing at
        // the failed one (slider updates went nowhere).
        if (terminalFiredRef.current) return;
        terminalFiredRef.current = true;
        setStatus("done");
        setTerminal(true);
        onDoneRef.current(ev);
        es.close();
      } else if (ev.type === "failed") {
        if (terminalFiredRef.current) return;
        terminalFiredRef.current = true;
        setStatus(`failed: ${ev.error}`);
        setTerminal(true);
        onFailedRef.current(ev);
        es.close();
      }
    }

    return () => {
      es.close();
    };
  }, [jobId]);

  const pct = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;
  const elapsedSec = jobStart ? Math.max(0, (nowTick - jobStart) / 1000) : 0;
  const avgStationSec =
    stationDurations.length > 0
      ? stationDurations.reduce((a, b) => a + b, 0) / stationDurations.length
      : null;
  const remaining = counts.total !== null ? Math.max(0, counts.total - counts.done) : null;
  const etaSec =
    avgStationSec !== null && remaining !== null && !terminal ? avgStationSec * remaining : null;

  return (
    <div style={card}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>JOB {jobId.slice(0, 8)}</div>
      <div style={{ fontSize: 13, marginBottom: 8 }}>{status}</div>
      <div style={bar}>
        <div style={{ ...barFill, width: `${pct}%` }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, marginTop: 8, fontSize: 11 }}>
        <Stat label="elapsed"  value={fmtDuration(elapsedSec)} />
        <Stat label="avg/station" value={avgStationSec !== null ? fmtDuration(avgStationSec) : "—"} />
        <Stat label="eta (approx)"    value={etaSec !== null ? fmtDuration(etaSec) : "—"} />
      </div>

      <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
        {counts.total !== null
          ? `${counts.done} / ${counts.total} stations`
          : "queued — waiting for worker…"}
      </div>

      {elapsedLog.length > 0 && (
        <ul style={{ marginTop: 8, padding: 0, listStyle: "none", fontSize: 11, color: "#888" }}>
          {elapsedLog.map((line, i) => (
            <li key={i} style={{ fontFamily: "monospace" }}>{line}</li>
          ))}
        </ul>
      )}

      {onCancel && !terminal && (
        <div style={{ marginTop: 10 }}>
          {!cancelArmed ? (
            <button
              style={cancelBtnIdle}
              onClick={() => setCancelArmed(true)}
              title="Cancel this job"
            >
              Cancel job
            </button>
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                style={cancelBtnConfirm}
                onClick={() => { onCancel(); setCancelArmed(false); }}
              >
                Confirm cancel
              </button>
              <button style={cancelBtnIdle} onClick={() => setCancelArmed(false)}>
                Keep running
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const cancelBtnIdle: React.CSSProperties = {
  width: "100%", padding: "6px 10px", fontSize: 12,
  background: "transparent", color: "#aaa",
  border: "1px solid #333", borderRadius: 4, cursor: "pointer",
};
const cancelBtnConfirm: React.CSSProperties = {
  flex: 1, padding: "6px 10px", fontSize: 12,
  background: "#a33", color: "white",
  border: 0, borderRadius: 4, cursor: "pointer",
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "#0e0e0e", border: "1px solid #222", borderRadius: 3, padding: "4px 6px" }}>
      <div style={{ color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: "#eee", fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}

function fmtDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${sec.toFixed(0)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return `${m}m ${s.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm.toString().padStart(2, "0")}m`;
}

const card: React.CSSProperties = {
  background: "#141414", border: "1px solid #2a2a2a", borderRadius: 4, padding: 12, marginTop: 12,
};
const bar: React.CSSProperties = {
  width: "100%", height: 4, background: "#222", borderRadius: 2, overflow: "hidden",
};
const barFill: React.CSSProperties = {
  height: "100%", background: "#3a7bd5", transition: "width 200ms",
};
